import sanitize from 'sanitize-html'

import {
  createDownloadLink,
  downloadFileFromServer,
  downloadFile as downloadPublicFile,
} from 'src/core/classes/helpers/downloadFile'

interface IAuthorizationHeader {
  authParams: string
}

interface IRequestData {
  url: string
  method: 'GET' | 'POST'
  authorization?: IAuthorizationHeader
}

interface IDownloadFileProps {
  fileName: string
  requestData: IRequestData
  onProgress?: (ev: ProgressEvent) => void
}

export const downloadFile = ({ fileName, requestData, onProgress }: IDownloadFileProps) => {
  fetchBase64({
    requestData,
    callback: (response) => {
      downloadFiles([
        {
          filename: fileName,
          download: response,
        },
      ])
    },
    onProgress,
  })
}

export const saveAndDownloadFile = async (url: string, fileName: string) => {
  const blob = await downloadFileFromServer(url)
  // Not a form-storage file (e.g. a Cloudinary link): download it without our token
  if (!blob) {
    return downloadPublicFile({ url, fileName })
  }
  const blobUrl = window.URL.createObjectURL(blob)
  createDownloadLink(blobUrl, fileName)
  window.URL.revokeObjectURL(blobUrl)
}

function downloadFiles(files: { download: string; filename: string }[]) {
  function download_next(i: any) {
    if (i >= files.length) {
      return
    }
    const a = document.createElement('a')
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.href = sanitize(files[i].download)
    // Use a.download if available, it prevents plugins from opening.
    if ('download' in a) {
      a.download = files[i].filename
    }
    // Add a to the doc for click to work.
    const el = document.body || document.documentElement
    el.appendChild(a)
    if (a.click) {
      a.click() // The click method is supported by most browsers.
    }
    // Delete the temporary link.
    if (a && a.parentNode) {
      a.parentNode.removeChild(a)
    }
    // Download the next file with a small timeout. The timeout is necessary
    // for IE, which will otherwise only download the first file.
    setTimeout(() => {
      download_next(i + 1)
    }, 3000)
  }
  // Initiate the first download.
  download_next(0)
}

interface IFetchBase64Props {
  requestData: IRequestData
  callback: (url: string) => void
  onProgress?: (ev: ProgressEvent) => void
}

export const fetchBase64 = ({ requestData, callback, onProgress }: IFetchBase64Props) => {
  const { url, method, authorization } = requestData
  const request = new XMLHttpRequest()
  request.open(method, url, true)
  request.responseType = 'blob'
  if (authorization) {
    const { authParams } = authorization
    request.setRequestHeader('Authorization', `Bearer ${authParams}`)
  }
  request.onload = () => {
    const reader = new FileReader()
    reader.readAsDataURL(request.response)
    reader.onload = (e: any) => {
      const blob = base64ToBlob(e.target.result)
      if (blob) {
        const localUrl = window.URL.createObjectURL(blob)
        callback(localUrl)
      }
    }
  }
  request.onprogress = onProgress || null
  request.send()
}

const base64ToBlob = (base64: string): Blob | null => {
  let result = null
  const regexp = /data:(.+);base64,(.+)/
  const match = base64.match(regexp)
  if (match && match[1] && match[2]) {
    // convert base64 string to byte array
    const byteCharacters = atob(match[2])
    const byteNumbers = [byteCharacters.length]
    for (let i = 0; i < byteCharacters.length; i = i + 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)

    // now that we have the byte array, construct the blob from it
    // @ts-ignore
    result = new Blob([byteArray], { type: match[1] })
  }

  return result
}

export const generateRandomInteger = (min: number, max: number) => {
  return Math.floor(min + Math.random() * (max + 1 - min))
}

export function composeURL(relative: string) {
  // const prefix = import.meta.env.VITE_PUBLIC_URL || ''
  //
  // if (prefix) {
  //   let url = `${prefix}${relative}`.replace(/\/\//, '/')
  //
  //   if (url !== '/') {
  //     url = url.replace(/\/$/, '')
  //   }
  //
  //   return url
  // }

  return relative
}

export const isProductionEnv = () => process.env.NODE_ENV === 'production'

export function isInstanceOf<T>(
  object: any,
  condition: string | ((object: any) => boolean)
): object is T {
  if (typeof condition === 'string') {
    return condition in object
  }

  return condition(object)
}
